/**
 * WI-B-02 — the /auth/callback result state machine (screen A4's brain).
 *
 * PURE, DOM-free. Takes the raw callback outcome (an auth code, or an error the
 * hosted page bounced back) plus a code→token exchange + a scope fetch, and
 * resolves to a discriminated result the shell routes to a screen:
 *   - "session"      → A4 success → land on Record            (has projects)
 *   - "no-projects"  → A6 (signed in, empty project_ids)      (never falls to Record)
 *   - "error"        → A5 with a `kind` (denied|expired|network)
 *
 * Derives-from: reference DOM A4 note ("code→token exchange AND scope bootstrap;
 * on success go straight to Record — never flash the shell; on failure route to
 * A5 with the matching kind"); B build-plan WI-B-02.
 *
 * The exchange + scope fns are INJECTED so this is testable against the mock
 * AuthKit (workos-mock authkitLogin) with zero network. The live leg (§S8) swaps
 * real implementations in with no change to this state machine.
 */

import { establishSession, type EstablishedSession, type SessionScope, type MembershipCheck } from "./workos-session";

/** Why a callback failed, mapped to A5's three bodies (reference DOM A5 note). */
export type AuthErrorKind =
  | "cancelled"
  | "denied"
  | "expired"
  | "invalid"
  | "superseded"
  | "rate-limited"
  | "network"
  | "failed";

/** The discriminated result the shell routes on. */
export type CallbackResult =
  | { kind: "session"; session: EstablishedSession }
  | { kind: "no-projects"; current_user: EstablishedSession["current_user"] }
  | { kind: "error"; error: AuthErrorKind };

/** What the hosted AuthKit page hands back to /callback: either an auth code, or
 * an error the user/hosted-page produced before we ever exchange anything. */
export type CallbackParams =
  | { code: string }
  | { error: "access_denied" | "expired_token" | string };

/** Exchange an auth code for a signed WorkOS token. Returns null on an auth
 * REFUSAL (no membership / access denied) — mirrors authkitLogin's null. Throws
 * on a transport/network failure (→ mapped to "network" below). */
export type CodeExchange = (code: string) => Promise<string | null>;

/** Fetch the provisioned scope (tenant + project_ids + bearer) for a verified
 * user. Throws on network failure (→ "network"). */
export type ScopeFetch = (user_id: string, org_id: string) => Promise<SessionScope>;

/** Map a bounced-back hosted-page error string to A5's kind. Unknown strings
 * are treated as `denied` — the safe default (offer "try again"), never a
 * silent success. */
function kindFromCallbackError(error: string): AuthErrorKind {
  if (error === "expired_token" || error === "expired") return "expired";
  if (error === "access_denied" || error === "cancelled") return "cancelled";
  if (error === "invalid_code" || error === "invalid") return "invalid";
  if (error === "code_superseded" || error === "superseded") return "superseded";
  if (error === "rate_limited" || error === "rate-limited") return "rate-limited";
  // Unknown provider outcomes fail closed and remain visibly unsuccessful.
  return "failed";
}

/**
 * Resolve a callback to the screen the shell should show. Never throws — every
 * failure path becomes an { kind: "error" } so the shell always has a screen
 * (an unhandled throw here would flash a blank shell, exactly what A4 forbids).
 */
export async function resolveCallback(
  params: CallbackParams,
  exchange: CodeExchange,
  fetchScope: ScopeFetch,
  checkMembership?: MembershipCheck,
): Promise<CallbackResult> {
  // The hosted page bounced an error back before we hold a code.
  if ("error" in params) {
    return { kind: "error", error: kindFromCallbackError(params.error) };
  }

  let token: string | null;
  try {
    token = await exchange(params.code);
  } catch {
    // Transport failure exchanging the code → network (retryable via A5).
    return { kind: "error", error: "network" };
  }
  // A null exchange is an auth REFUSAL (no membership) — the code was real but
  // the user has no access. "denied", not "network": retrying the same identity
  // won't help; the fix is being added to a project.
  if (token === null) {
    return { kind: "error", error: "denied" };
  }

  // Verify + scope. A bad/expired token throws out of establishSession → treat
  // an expired/invalid token as "expired" (send yourself a fresh link).
  let session: EstablishedSession;
  try {
    const scope = await fetchScopeSafely(token, fetchScope);
    session = establishFrom(token, scope, checkMembership);
  } catch (e) {
    return { kind: "error", error: errorKindFromThrow(e) };
  }

  // Signed in but assigned to no project → A6, NEVER a projectless Record.
  if (session.config.project_ids.length === 0) {
    return { kind: "no-projects", current_user: session.current_user };
  }
  return { kind: "session", session };
}

/** A distinguishable network error the scope-fetch throws, so a scope-fetch
 * failure maps to "network" while a bad TOKEN maps to "expired". */
export class ScopeNetworkError extends Error {}

async function fetchScopeSafely(token: string, fetchScope: ScopeFetch): Promise<SessionScope> {
  // Verify first (cheap, deterministic) to pull user_id/org_id for the scope
  // call. If the token is bad, establishSession below throws the same way; we
  // verify here only to get identity for the scope fetch. A network failure in
  // the scope fetch is tagged so it maps to "network", not "expired".
  const { verifyAndMapWorkosToken } = await import("./workos-identity");
  const u = verifyAndMapWorkosToken(token); // throws → bad token → "expired"
  try {
    return await fetchScope(u.user_id, u.org_id);
  } catch (e) {
    throw new ScopeNetworkError(e instanceof Error ? e.message : "scope fetch failed");
  }
}

function establishFrom(token: string, scope: SessionScope, checkMembership?: MembershipCheck): EstablishedSession {
  return establishSession(token, scope, checkMembership);
}

function errorKindFromThrow(e: unknown): AuthErrorKind {
  // A tagged scope-fetch failure → network; anything else (a token that failed
  // verification) → expired.
  if (e instanceof ScopeNetworkError) return "network";
  return "expired";
}
